import { useState } from 'react';

import { useTranslation } from '@/utils/i18n';
import { useGroups } from '@/hooks/queries/groups';
import { toast } from '@/stores/toast';
import BottomSheet from './BottomSheet';
import { PrimaryButton } from '@/components/Buttons';
import CircleEmojiField from '@/components/CircleEmojiField';
import type { Group } from '@/types';

/**
 * "Créer un cercle" bottom sheet (wireframe 3b): name + emoji + create.
 */
export default function CreateCircleSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (group: Group) => void;
}) {
  const { t } = useTranslation();
  const { createGroup, isCreating } = useGroups();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState<string>('⭐');

  const reset = () => {
    setName('');
    setEmoji('⭐');
  };

  const submit = async () => {
    if (!name.trim()) return;
    try {
      const res = await createGroup({ name: name.trim(), emoji, visibility: 'private' });
      toast.success(t('groups.create.success'));
      reset();
      onClose();
      onCreated?.(res.data);
    } catch {
      toast.error(t('groups.create.error'));
    }
  };

  return (
    <BottomSheet
      open={open}
      title={t('groups.create.title')}
      onClose={() => {
        reset();
        onClose();
      }}
      testid="create-circle-sheet"
    >
      <div className="tw:flex tw:flex-col tw:gap-5 tw:px-1 tw:pb-2">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <label className="tw:text-[13px] tw:font-semibold tw:text-grey-2">
            {t('groups.create.nameLabel')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('groups.create.namePlaceholder')}
            data-testid="input-circle-name"
            maxLength={40}
            className="tw:h-12 tw:w-full tw:rounded-2xl tw:border tw:border-white/10 tw:bg-dark-primary tw:px-4 tw:text-[15px] tw:text-white tw:placeholder:text-grey-4 tw:focus:outline-none"
          />
        </div>

        <CircleEmojiField value={emoji} onChange={setEmoji} />

        <PrimaryButton
          label={t('groups.create.submit')}
          onClick={submit}
          disabled={!name.trim() || isCreating}
          loading={isCreating}
          data-testid="btn-create-circle"
        />
      </div>
    </BottomSheet>
  );
}
